<script setup lang="ts">
import { computed } from "vue";
import Avatar from "@/components/ui/Avatar.vue";

/**
 * The round avatar of a user: their picture, or their initials when they have none.
 *
 * Takes the user as the API gives it, whichever resource it comes from: UserResource
 * names the picture `avatar` and carries both `firstname` and `first_name`, the header
 * reads `avatar_url`, and an account (an activity causer) only has `first_name`.
 */
interface AvatarUser {
  firstname?: string | null;
  lastname?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  avatar?: string | null;
  avatar_url?: string | null;
}

const props = withDefaults(
  defineProps<{
    user: AvatarUser;
    size?: number;
  }>(),
  {
    size: 30,
  }
);

// An invited account holds no name until its owner gives one, so the email stands in.
const username = computed(() => {
  const name = [
    props.user.firstname ?? props.user.first_name,
    props.user.lastname ?? props.user.last_name,
  ].filter(Boolean).join(" ");

  return name || props.user.email || "";
});

const imgSrc = computed(() => props.user.avatar ?? props.user.avatar_url ?? "");
</script>

<template>
  <Avatar :username="username" :img-src="imgSrc" :size="size" rounded class="tw:shrink-0" />
</template>
